/**
 * FlagInboxTab
 *
 * Super-admin only org-wide flag review queue. Lists every UserFlag in the
 * system grouped by status (open / cleared / compromised) and lets the
 * super admin clear or escalate any open flag to "compromised" — the same
 * actions exposed inline in FlagHistorySection on the per-user detail
 * modal, but surfaced in a single dashboard so flags can be triaged
 * without having to know which user to open first.
 *
 * Phase 2 — see docs/audit-log-roadmap.md:
 *   - Real-time in-app notification fan-out when a new flag is filed.
 *     For now this tab is the canonical surface; it polls on demand via
 *     the Refresh button.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiFlag, FiCheck, FiAlertOctagon, FiUser, FiMail, FiLock,
} from 'react-icons/fi';
import { userFlagsAPI } from '../../services/api';
import RefreshButton from '../common/RefreshButton';
import Button from '../common/Button';

function fmtDate(d) {
  try {
    return new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch (e) {
    return String(d);
  }
}

function StatusPill({ status }) {
  const map = {
    open: { bg: '#f3ebd7', color: '#8a6a1f', label: 'OPEN' },
    cleared: { bg: '#e8efe5', color: '#4a6b3f', label: 'CLEARED' },
    compromised: { bg: '#f2e9e6', color: '#9a564a', label: 'COMPROMISED' },
  };
  const s = map[status] || map.open;
  return (
    <span className="inline-flex items-center px-2 py-[2px] rounded-full text-[9.5px] font-bold" style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

const STATUS_TABS = [
  { id: 'open', label: 'Open', color: '#8a6a1f' },
  { id: 'cleared', label: 'Cleared', color: '#4a6b3f' },
  { id: 'compromised', label: 'Compromised', color: '#9a564a' },
  { id: 'all', label: 'All', color: '#7a7060' },
];

export default function FlagInboxTab({ currentUserRole }) {
  const isSuperAdmin = currentUserRole === 'super_admin';

  const [statusFilter, setStatusFilter] = useState('open');
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Per-row action state
  const [activeFlagId, setActiveFlagId] = useState(null); // which flag's notes input is expanded
  const [busyFlag, setBusyFlag] = useState(null);
  const [notes, setNotes] = useState('');
  const [showCompromiseConfirm, setShowCompromiseConfirm] = useState(null);

  const load = useCallback(async () => {
    if (!isSuperAdmin) return;
    setLoading(true);
    setError('');
    try {
      // API treats empty status as "no filter" (controller checks `if (req.query.status)`).
      // Pass '' explicitly to bypass listOpen's default of 'open' for the "all" tab.
      const param = statusFilter === 'all' ? '' : statusFilter;
      const res = await userFlagsAPI.listOpen(param);
      setFlags(res?.data?.flags || []);
    } catch (err) {
      setError(err?.message || 'Failed to load flags');
      setFlags([]);
    } finally {
      setLoading(false);
    }
  }, [isSuperAdmin, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const counts = useMemo(() => {
    const c = { open: 0, cleared: 0, compromised: 0 };
    flags.forEach((f) => { if (c[f.status] !== undefined) c[f.status] += 1; });
    return c;
  }, [flags]);

  const startAction = (flagId) => {
    if (activeFlagId === flagId) {
      setActiveFlagId(null);
      setNotes('');
    } else {
      setActiveFlagId(flagId);
      setNotes('');
    }
  };

  const clear = async (flagId) => {
    setBusyFlag(flagId);
    setError('');
    try {
      await userFlagsAPI.clear(flagId, notes || undefined);
      setNotes('');
      setActiveFlagId(null);
      await load();
    } catch (err) {
      setError(err?.message || 'Failed to clear flag');
    } finally {
      setBusyFlag(null);
    }
  };

  const compromise = async (flagId) => {
    setBusyFlag(flagId);
    setError('');
    try {
      await userFlagsAPI.markCompromised(flagId, notes || undefined);
      setNotes('');
      setActiveFlagId(null);
      setShowCompromiseConfirm(null);
      await load();
    } catch (err) {
      setError(err?.message || 'Failed to mark compromised');
    } finally {
      setBusyFlag(null);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="bg-white rounded-[14px] border p-8 text-center" style={{ borderColor: '#e8e2d6' }}>
        <FiLock className="text-[28px] mx-auto mb-2 text-[#9a9080]" />
        <h3 className="text-[16px] font-bold text-[#08111f] mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          Super-Admin Only
        </h3>
        <p className="text-[12.5px] text-[#7a7060]">
          The Flag Inbox is restricted to super admins. Regular admins can file flags from any user's detail modal — they appear here for super-admin review.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[14px] border" style={{ borderColor: '#e8e2d6' }}>
      {/* Action row — page title + subhead live in AdminDashboardPage's top
          header ("Flag Inbox / Triage flags filed by admins…"). The card
          keeps just the refresh affordance flush right above the filter row. */}
      <div className="px-5 py-3 border-b flex items-center justify-end flex-wrap gap-3" style={{ borderColor: '#e8e2d6' }}>
        <RefreshButton onClick={load} loading={loading} />
      </div>

      {/* Status filter tabs */}
      <div className="px-5 pt-3 pb-0 border-b flex items-center gap-1 flex-wrap" style={{ borderColor: '#e8e2d6' }}>
        {STATUS_TABS.map((t) => {
          const active = statusFilter === t.id;
          const count = t.id === 'all' ? flags.length : counts[t.id];
          return (
            <button
              key={t.id}
              onClick={() => setStatusFilter(t.id)}
              className="px-3 py-2 text-[12px] font-semibold border-b-2 transition-colors"
              style={{
                color: active ? t.color : '#9a9080',
                borderColor: active ? t.color : 'transparent',
              }}
            >
              {t.label}
              {statusFilter !== 'all' && t.id !== 'all' && t.id === statusFilter && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold" style={{ background: `${t.color}1a`, color: t.color }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Error */}
      {error && (
        <div className="mx-5 mt-3 text-[12px] px-3 py-2 rounded-lg border" style={{ background: '#f2e9e6', borderColor: '#e6d2cb', color: '#9a564a' }}>{error}</div>
      )}

      {/* List */}
      <div className="p-5 space-y-2">
        {loading && flags.length === 0 && (
          <div className="text-[12px] italic text-[#7a7060] animate-pulse text-center py-6">Loading flags…</div>
        )}
        {!loading && flags.length === 0 && (
          <div className="text-center py-10">
            <FiFlag className="text-[28px] mx-auto mb-2 text-[#cbbf9a]" />
            <div className="text-[13px] text-[#7a7060]">
              No {statusFilter !== 'all' ? statusFilter : ''} flags right now.
            </div>
            <div className="text-[11.5px] text-[#9a9080] mt-0.5">
              {statusFilter === 'open'
                ? 'Nothing waiting for review — nice.'
                : 'Try a different status filter.'}
            </div>
          </div>
        )}

        {flags.map((f) => {
          const open = f.status === 'open';
          const isActive = activeFlagId === f._id;
          const u = f.user || {};
          return (
            <div
              key={f._id}
              className="border rounded-[10px] overflow-hidden"
              style={{ borderColor: '#e8e2d6', background: '#fdfcf9' }}
            >
              <div className="px-3 py-3 border-b" style={{ borderColor: '#e8e2d6' }}>
                <div className="flex items-start gap-2">
                  <FiFlag className="text-[14px] mt-1 shrink-0" style={{ color: open ? '#b8973a' : '#9a9080' }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <StatusPill status={f.status} />
                      <span className="text-[12.5px] font-bold text-[#08111f] flex items-center gap-1">
                        <FiUser className="text-[11px] text-[#7a7060]" />
                        {u.fullName || '(deleted user)'}
                      </span>
                      {u.email && (
                        <span className="text-[11px] text-[#7a7060] flex items-center gap-1">
                          <FiMail className="text-[10px]" />
                          {u.email}
                        </span>
                      )}
                      {u.roleName && (
                        <span className="text-[10px] uppercase tracking-wider text-[#9a9080] border rounded px-1.5 py-[1px]" style={{ borderColor: '#e8e2d6' }}>
                          {u.roleName}
                        </span>
                      )}
                      {u.isActive === false && (
                        <span className="text-[10px] font-bold rounded px-1.5 py-[1px]" style={{ color: '#9a564a', background: '#f2e9e6' }}>DEACTIVATED</span>
                      )}
                    </div>
                    <div className="text-[11px] text-[#7a7060]">
                      Filed by <strong className="text-[#08111f]">{f.reporterName || 'unknown'}</strong>
                      {f.reporterRole && <span className="text-[#9a9080]"> ({f.reporterRole})</span>}
                      <span> · {fmtDate(f.createdAt)}</span>
                    </div>
                    <div className="text-[12px] text-[#08111f] mt-1.5 leading-snug whitespace-pre-wrap">
                      {f.reason}
                    </div>
                    {!open && f.resolvedByName && (
                      <div className="text-[10.5px] text-[#7a7060] mt-1.5 italic">
                        Resolved by <strong>{f.resolvedByName}</strong> on {fmtDate(f.resolvedAt)}
                        {f.resolutionNotes && <span> — {f.resolutionNotes}</span>}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {open && (
                <div className="px-3 py-2.5">
                  {!isActive ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button variant="outline" size="sm" onClick={() => startAction(f._id)}>
                        Review &amp; Resolve
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Resolution notes (optional, saved to audit log)"
                        rows={2}
                        maxLength={1000}
                        className="w-full border rounded-[8px] px-2.5 py-1.5 text-[11.5px] outline-none focus:border-[#145f5f40]"
                        style={{ borderColor: '#ddd6c6' }}
                      />
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button
                          variant="success"
                          size="sm"
                          leftIcon={<FiCheck className="text-[11px]" />}
                          loading={busyFlag === f._id}
                          onClick={() => clear(f._id)}
                        >
                          Clear Flag
                        </Button>
                        <Button
                          variant="danger"
                          size="sm"
                          leftIcon={<FiAlertOctagon className="text-[11px]" />}
                          disabled={busyFlag === f._id}
                          onClick={() => setShowCompromiseConfirm(f)}
                        >
                          Mark as Compromised
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setActiveFlagId(null); setNotes(''); }}
                        >
                          Cancel
                        </Button>
                      </div>
                      <div className="text-[10px] italic text-[#7a7060]">
                        "Mark as Compromised" deactivates the user, revokes all sessions, and stamps <code>compromisedAt</code>. The flag transition + cascaded actions share one request ID in the audit log.
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Compromise confirmation modal */}
      {showCompromiseConfirm && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center"
          style={{ background: 'rgba(8,17,31,0.65)' }}
          onClick={() => setShowCompromiseConfirm(null)}
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-[14px] max-w-[460px] w-full mx-4 shadow-2xl">
            <div className="px-5 py-4 border-b" style={{ borderColor: '#e8e2d6', background: 'linear-gradient(180deg,#f2e9e6 0%,#ffffff 100%)' }}>
              <div className="flex items-center gap-2">
                <FiAlertOctagon className="text-[18px]" style={{ color: '#9a564a' }} />
                <h3 className="text-[15px] font-bold text-[#08111f]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Mark Account as Compromised
                </h3>
              </div>
            </div>
            <div className="p-5 space-y-2 text-[12px] text-[#08111f]">
              <p>
                Target user:{' '}
                <strong>{showCompromiseConfirm.user?.fullName || '(unknown)'}</strong>{' '}
                <span className="text-[#7a7060]">({showCompromiseConfirm.user?.email || '—'})</span>
              </p>
              <p>This will:</p>
              <ul className="list-disc list-inside text-[11.5px] space-y-1 text-[#7a7060]">
                <li>Set the flag status to <strong>compromised</strong></li>
                <li>Deactivate the user account (cannot sign in)</li>
                <li>Revoke all active sessions on every device</li>
                <li>Stamp <code>compromisedAt</code> so the UI shows a red "Compromised" pill</li>
                <li>Write three correlated audit rows under one request ID</li>
              </ul>
              <p className="text-[11.5px] italic mt-2">
                This action cannot be undone via the UI — to reactivate, you'd need to clear deactivation manually from the user detail modal.
              </p>
            </div>
            <div className="px-5 py-3 border-t flex items-center justify-end gap-2" style={{ borderColor: '#e8e2d6' }}>
              <Button variant="outline" onClick={() => setShowCompromiseConfirm(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={busyFlag === showCompromiseConfirm._id}
                onClick={() => compromise(showCompromiseConfirm._id)}
              >
                Yes, mark as compromised
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
